import { useCallback, useEffect, useState, type JSX } from 'react';
import { useRouteLoaderData } from '@remix-run/react';
import type { loader } from '~/root.tsx';
import { ALERT_MODAL_DEFAULT_CLOSE_ASSET } from '~/services/plugins/default_theme_v2/config/alert-modal-fields.ts';
import {
  ALERT_MODAL_EVENT,
  isAlertModalDetail,
  subscribeAlertModal,
  type AlertModalRequest,
} from '~/utils/alert-modal.ts';
import { AlertModal } from './alert-modal.tsx';

export const DEFAULT_CLOSE_ICON = ALERT_MODAL_DEFAULT_CLOSE_ASSET;

/**
 * Resolves the Alert Modal close icon URL, falling back when the theme value is missing or empty.
 */
export function getAlertModalCloseIconUrl(
  closeIcon: string | undefined | null
): string {
  return closeIcon || DEFAULT_CLOSE_ICON;
}

/**
 * Global host that renders theme-styled Alert Modals app-wide.
 */
export function AlertModalHost(): JSX.Element | null {
  const rootData = useRouteLoaderData<typeof loader>('root');
  const closeIconUrl = getAlertModalCloseIconUrl(rootData?.alertModalCloseIcon);
  const [request, setRequest] = useState<AlertModalRequest | null>(null);

  const close = useCallback((confirmed: boolean): void => {
    setRequest((currentRequest) => {
      currentRequest?.resolve(confirmed);
      return null;
    });
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeAlertModal((nextRequest) => {
      setRequest((currentRequest) => {
        currentRequest?.resolve(false);
        return nextRequest;
      });
    });

    const handleEvent = (event: Event): void => {
      const customEvent = event as CustomEvent<unknown>;
      if (!isAlertModalDetail(customEvent.detail)) {
        return;
      }

      const detail = customEvent.detail;

      setRequest((currentRequest) => {
        currentRequest?.resolve(false);
        return {
          ...detail,
          resolve: () => undefined,
        };
      });
    };

    window.addEventListener(ALERT_MODAL_EVENT, handleEvent);

    return () => {
      unsubscribe();
      window.removeEventListener(ALERT_MODAL_EVENT, handleEvent);
    };
  }, []);

  useEffect(() => {
    if (!request) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close(false);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [request, close]);

  if (!request) return null;

  return (
    <AlertModal
      title={request.title}
      message={request.message}
      primaryButtonLabel={request.primaryButtonLabel}
      secondaryButtonLabel={request.secondaryButtonLabel}
      closeIconUrl={closeIconUrl}
      onConfirm={() => close(true)}
      onCancel={() => close(false)}
    />
  );
}
